import z from "zod";

import type { PrimitiveDataType } from "./primitive-data-type";

/**
 * Scale convention for `PERCENT` formats.
 *
 * - `"0-1"` (default): values are fractions and the renderer multiplies by 100
 *   (e.g. `0.42` -> `42%`).
 * - `"0-100"`: values are already whole percentages and are shown as-is with a
 *   trailing `%` (e.g. `42` -> `42%`).
 */
export const PercentScaleSchema = z.enum(["0-1", "0-100"]);
export type PercentScale = z.infer<typeof PercentScaleSchema>;

export const PERCENT_SCALE_LABELS: Record<PercentScale, string> = {
  "0-1": "0–1",
  "0-100": "0–100",
};

/**
 * Single date format. Each value implies both how the value renders **and** how
 * it buckets when the column is grouped/charted (SQL `DATE_TRUNC`), so the user
 * makes exactly one selection. The set is guided by DuckDB's temporal types.
 *
 * - `DATETIME` — "Timestamp", no truncation (full timestamp)
 * - `DATE` — "Date", truncates to `day`
 * - `TIME` — "Time", no truncation
 * - `WEEK` — "Week", truncates to `week`
 * - `MONTH` — "Month", truncates to `month`
 * - `QUARTER` — "Quarter", truncates to `quarter`
 * - `YEAR` — "Year", truncates to `year`
 * - `RELATIVE` — "Relative" (e.g. "3 days ago"), no truncation
 */
export const DateFormatSchema = z.enum([
  "DATETIME",
  "DATE",
  "TIME",
  "WEEK",
  "MONTH",
  "QUARTER",
  "YEAR",
  "RELATIVE",
]);
export type DateFormat = z.infer<typeof DateFormatSchema>;

/**
 * Legacy date truncation grain. No longer part of `ColumnFormat`; retained only
 * to upconvert the old `{ grain, display }` shape to `DateFormat`.
 * LEGACY BACK-COMPAT (removable) — see the removal guide below.
 */
const DateGrainSchema = z.enum([
  "NONE",
  "DAY",
  "WEEK",
  "MONTH",
  "QUARTER",
  "YEAR",
]);
type DateGrain = z.infer<typeof DateGrainSchema>;

/**
 * Legacy date presentation. No longer part of `ColumnFormat`; retained only to
 * upconvert the old `{ grain, display }` shape to `DateFormat`.
 * LEGACY BACK-COMPAT (removable) — see the removal guide below.
 */
const DateDisplaySchema = z.enum([
  "AUTO",
  "DATE",
  "DATETIME",
  "TIME",
  "RELATIVE",
  "WEEK_RANGE",
  "MONTH_YEAR",
  "QUARTER_YEAR",
  "YEAR",
]);
type DateDisplay = z.infer<typeof DateDisplaySchema>;

/**
 * Discriminated union describing how a column's values should be displayed.
 * The `kind` tag selects the variant; each variant carries only the options
 * that are meaningful for it, so invalid combinations (e.g. a percent scale on
 * a text column) are unrepresentable.
 */
export const ColumnFormatSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("NUMBER"),
    decimals: z.number().int().min(0).optional(),
  }),
  z.object({ kind: z.literal("NUMBER_PLAIN") }),
  z.object({
    kind: z.literal("PERCENT"),
    scale: PercentScaleSchema.default("0-1"),
    decimals: z.number().int().min(0).optional(),
  }),
  z.object({
    kind: z.literal("CURRENCY"),
    symbol: z.string().optional(),
    code: z.string().optional(),
  }),
  z.object({ kind: z.literal("TEXT") }),
  z.object({ kind: z.literal("EMAIL") }),
  z.object({ kind: z.literal("URL") }),
  z.object({ kind: z.literal("IMAGE") }),
  z.object({ kind: z.literal("ENUM") }),
  z.object({ kind: z.literal("BOOLEAN") }),
  z.object({ kind: z.literal("OBJECT") }),
  z.object({ kind: z.literal("ARRAY") }),
  z.object({
    kind: z.literal("DATE"),
    dateFormat: DateFormatSchema.default("DATE"),
  }),
]);
export type ColumnFormat = z.infer<typeof ColumnFormatSchema>;

/** The bare discriminant values, for label/icon maps that only need the kind. */
export const ColumnFormatKindSchema = z.enum([
  "NUMBER",
  "NUMBER_PLAIN",
  "PERCENT",
  "CURRENCY",
  "TEXT",
  "EMAIL",
  "URL",
  "IMAGE",
  "ENUM",
  "BOOLEAN",
  "OBJECT",
  "ARRAY",
  "DATE",
]);
export type ColumnFormatKind = z.infer<typeof ColumnFormatKindSchema>;

export const FORMAT_KIND_LABELS: Record<ColumnFormatKind, string> = {
  NUMBER: "Number",
  NUMBER_PLAIN: "Number plain",
  PERCENT: "Percent",
  CURRENCY: "Currency",
  TEXT: "Text",
  EMAIL: "Email",
  URL: "URL",
  IMAGE: "Image",
  ENUM: "Enum",
  BOOLEAN: "Boolean",
  OBJECT: "Object",
  ARRAY: "Array",
  DATE: "Date",
};

export const DATE_FORMAT_LABELS: Record<DateFormat, string> = {
  DATETIME: "Timestamp",
  DATE: "Date",
  TIME: "Time",
  WEEK: "Week",
  MONTH: "Month",
  QUARTER: "Quarter",
  YEAR: "Year",
  RELATIVE: "Relative",
};

/** Date formats offered in the on-chart granularity picker. */
export const DATE_GRANULARITY_FORMATS: DateFormat[] = ["DATE", "MONTH", "YEAR"];

/**
 * Maps a `ColumnFormat` to its underlying primitive data type. Used when a
 * consumer needs to branch on the storage/engine type rather than the display
 * kind.
 */
export const columnFormatToPrimitiveType = (
  format: ColumnFormat | undefined,
): PrimitiveDataType => {
  if (!format) return "STRING";
  switch (format.kind) {
    case "NUMBER":
    case "NUMBER_PLAIN":
    case "PERCENT":
    case "CURRENCY":
      return "NUMBER";
    case "DATE":
      return "DATE";
    case "BOOLEAN":
      return "BOOLEAN";
    case "OBJECT":
      return "OBJECT";
    case "ARRAY":
      return "ARRAY";
    case "TEXT":
    case "EMAIL":
    case "URL":
    case "IMAGE":
    case "ENUM":
      return "STRING";
  }
};

/** Returns the default `ColumnFormat` for a primitive data type. */
export const defaultFormatForPrimitive = (
  primitive: PrimitiveDataType,
): ColumnFormat => {
  switch (primitive) {
    case "ARRAY":
      return { kind: "ARRAY" };
    case "BOOLEAN":
      return { kind: "BOOLEAN" };
    case "DATE":
      return { kind: "DATE", dateFormat: "DATE" };
    case "NUMBER":
      return { kind: "NUMBER" };
    case "OBJECT":
      return { kind: "OBJECT" };
    case "STRING":
      return { kind: "TEXT" };
  }

  return { kind: "TEXT" };
};

/** True when the format renders a date/time value. */
export const isDateFormat = (
  format: ColumnFormat | undefined,
): format is Extract<ColumnFormat, { kind: "DATE" }> => format?.kind === "DATE";

/** True when the format renders a numeric value. */
export const isNumberFormat = (format: ColumnFormat | undefined): boolean =>
  format != null &&
  (format.kind === "NUMBER" ||
    format.kind === "NUMBER_PLAIN" ||
    format.kind === "PERCENT" ||
    format.kind === "CURRENCY");

/** True when the format renders a textual value. */
export const isTextFormat = (format: ColumnFormat | undefined): boolean =>
  format != null &&
  (format.kind === "TEXT" ||
    format.kind === "EMAIL" ||
    format.kind === "URL" ||
    format.kind === "IMAGE" ||
    format.kind === "ENUM");

/**
 * Maps a `DateFormat` to its DuckDB `DATE_TRUNC` unit, or null when no bucketing
 * applies (full timestamp, time-of-day, or relative).
 */
export const dateFormatToTruncUnit = (
  dateFormat: DateFormat,
): string | null => {
  switch (dateFormat) {
    case "DATE":
      return "day";
    case "WEEK":
      return "week";
    case "MONTH":
      return "month";
    case "QUARTER":
      return "quarter";
    case "YEAR":
      return "year";
    case "DATETIME":
    case "TIME":
    case "RELATIVE":
      return null;
  }
};

// ---------------------------------------------------------------------------
// LEGACY BACK-COMPAT (removable)
//
// Everything from here down to `legacyFormatToColumnFormat` (plus the legacy
// `else` branch in `coerceColumnFormat` below and the legacy half of the
// `z.preprocess` in `packages/shared/rift/types.ts`) exists only to upconvert
// the old persisted `{ formatType, formatOptions }` shape to `ColumnFormat`.
//
// The `"kind" in raw` branch of `coerceColumnFormat` is NOT legacy — it
// validates the union shape and applies schema defaults, so keep it.
//
// To remove this legacy code safely, ALL of these must be true first:
//   1. Deploy the current code (coerces at every read boundary).
//   2. Run the backfill: `pnpm --filter @repo/scripts migrate-column-format`
//      — rewrites blocks.columnsConfig + datasets.columnsConfig in Postgres.
//   3. Invalidate client caches: bump the IndexedDB `version` in
//      `apps/web/src/libs/legend-state.ts` AND list the affected stores in
//      `deleteTableNames`. A version bump ALONE does NOT clear data — the
//      plugin's `onupgradeneeded` only creates missing stores; `deleteTableNames`
//      is what `deleteObjectStore`s the stale tables on upgrade so they recreate
//      empty (the v2 -> v3 bump already drops `datasets`/`blocks` this way).
//      Blocks/datasets are persisted client-side, so the server backfill alone
//      does NOT clear legacy data — stale IndexedDB still feeds the read
//      boundaries until the cache resets.
//   4. Let it ride one release so rollbacks / stragglers are flushed.
// This also covers the intermediate `{ kind: "DATE", grain, display }` shape:
// `collapseDateFormat` folds those two fields into the single `dateFormat`, and
// the `"kind" in raw` branch of `coerceColumnFormat` applies it on read.
//
// Then delete: `LEGACY_DATE_FORMATS`, `SIMPLE_LEGACY_KINDS`,
// `legacyFormatToColumnFormat`, `collapseDateFormat`, `DateGrainSchema`,
// `DateDisplaySchema`, the legacy branches in `coerceColumnFormat`, and simplify
// the `ColumnConfigSchema` preprocess to a plain `ColumnFormatSchema`.
// ---------------------------------------------------------------------------

/** Folds the legacy `{ grain, display }` pair into a single `DateFormat`. */
const collapseDateFormat = (
  grain: DateGrain | undefined,
  display: DateDisplay | undefined,
): DateFormat => {
  switch (display) {
    case "DATETIME":
      return "DATETIME";
    case "TIME":
      return "TIME";
    case "RELATIVE":
      return "RELATIVE";
    case "DATE":
      return "DATE";
    case "WEEK_RANGE":
      return "WEEK";
    case "MONTH_YEAR":
      return "MONTH";
    case "QUARTER_YEAR":
      return "QUARTER";
    case "YEAR":
      return "YEAR";
    default:
      // `AUTO` / missing — derive from the grain.
      switch (grain) {
        case "WEEK":
          return "WEEK";
        case "MONTH":
          return "MONTH";
        case "QUARTER":
          return "QUARTER";
        case "YEAR":
          return "YEAR";
        default:
          return "DATE";
      }
  }
};

const LEGACY_DATE_FORMATS: Record<string, ColumnFormat> = {
  TIMESTAMP: { kind: "DATE", dateFormat: "DATETIME" },
  TIME: { kind: "DATE", dateFormat: "TIME" },
  TIMESINCE: { kind: "DATE", dateFormat: "RELATIVE" },
  DAY: { kind: "DATE", dateFormat: "DATE" },
  WEEK: { kind: "DATE", dateFormat: "WEEK" },
  MONTH: { kind: "DATE", dateFormat: "MONTH" },
  QUARTER: { kind: "DATE", dateFormat: "QUARTER" },
  YEAR: { kind: "DATE", dateFormat: "YEAR" },
};

const SIMPLE_LEGACY_KINDS = new Set([
  "NUMBER",
  "NUMBER_PLAIN",
  "TEXT",
  "EMAIL",
  "URL",
  "IMAGE",
  "ENUM",
  "BOOLEAN",
  "OBJECT",
  "ARRAY",
]);

/**
 * Upconverts the legacy `{ formatType, formatOptions }` shape into a
 * `ColumnFormat`. Returns `undefined` when no format was set.
 */
export const legacyFormatToColumnFormat = (
  formatType: string | null | undefined,
  formatOptions?: {
    percentScale?: string | null;
    currencySymbol?: string | null;
  } | null,
): ColumnFormat | undefined => {
  if (!formatType) return undefined;

  const legacyDate = LEGACY_DATE_FORMATS[formatType];
  if (legacyDate) return legacyDate;

  if (formatType === "PERCENT") {
    return {
      kind: "PERCENT",
      scale: formatOptions?.percentScale === "0-100" ? "0-100" : "0-1",
    };
  }

  if (formatType === "CURRENCY") {
    return formatOptions?.currencySymbol
      ? { kind: "CURRENCY", symbol: formatOptions.currencySymbol }
      : { kind: "CURRENCY" };
  }

  if (SIMPLE_LEGACY_KINDS.has(formatType)) {
    return { kind: formatType } as ColumnFormat;
  }

  return undefined;
};

/**
 * Normalizes an unknown persisted value into a `ColumnFormat`, accepting either
 * the current discriminated-union shape or the legacy `{ formatType,
 * formatOptions }` shape. Returns `undefined` when nothing valid is present.
 */
export const coerceColumnFormat = (raw: unknown): ColumnFormat | undefined => {
  if (raw == null || typeof raw !== "object") return undefined;

  if ("kind" in raw) {
    // LEGACY BACK-COMPAT (removable) — collapse the old DATE `{ grain, display }`
    // pair into the single `dateFormat` before validating.
    if (
      (raw as { kind?: unknown }).kind === "DATE" &&
      !("dateFormat" in raw) &&
      ("grain" in raw || "display" in raw)
    ) {
      const legacy = raw as { grain?: DateGrain; display?: DateDisplay };
      return {
        kind: "DATE",
        dateFormat: collapseDateFormat(legacy.grain, legacy.display),
      };
    }

    const parsed = ColumnFormatSchema.safeParse(raw);
    return parsed.success ? parsed.data : undefined;
  }

  // LEGACY BACK-COMPAT (removable) — see the removal guide above
  // `LEGACY_DATE_FORMATS`.
  const legacy = raw as {
    formatType?: string | null;
    formatOptions?: {
      percentScale?: string | null;
      currencySymbol?: string | null;
    } | null;
  };
  return legacyFormatToColumnFormat(legacy.formatType, legacy.formatOptions);
};

/**
 * Agent-facing variant of `ColumnFormatSchema`. Per the slim-schema convention,
 * optional members are required + nullable so the model always emits a concrete
 * value (or `null` to keep the default).
 */
export const ColumnFormatAgentSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("NUMBER"),
    decimals: z.number().int().min(0).nullable(),
  }),
  z.object({ kind: z.literal("NUMBER_PLAIN") }),
  z.object({
    kind: z.literal("PERCENT"),
    scale: PercentScaleSchema.nullable().describe(
      "'0-1' if values are fractions (e.g. 0.42), '0-100' if values are already whole percentages (e.g. 42). null keeps the default (0-1).",
    ),
    decimals: z.number().int().min(0).nullable(),
  }),
  z.object({
    kind: z.literal("CURRENCY"),
    symbol: z
      .string()
      .nullable()
      .describe("Currency symbol/prefix (e.g. '€', '£'). null keeps '$'."),
    code: z.string().nullable(),
  }),
  z.object({ kind: z.literal("TEXT") }),
  z.object({ kind: z.literal("EMAIL") }),
  z.object({ kind: z.literal("URL") }),
  z.object({ kind: z.literal("IMAGE") }),
  z.object({ kind: z.literal("ENUM") }),
  z.object({ kind: z.literal("BOOLEAN") }),
  z.object({ kind: z.literal("OBJECT") }),
  z.object({ kind: z.literal("ARRAY") }),
  z.object({
    kind: z.literal("DATE"),
    dateFormat: DateFormatSchema.nullable().describe(
      "How the date renders and buckets when grouped: DATETIME (full timestamp), DATE (day), TIME, WEEK, MONTH, QUARTER, YEAR, or RELATIVE (e.g. '3 days ago'). null keeps DATE.",
    ),
  }),
]);
export type ColumnFormatAgent = z.infer<typeof ColumnFormatAgentSchema>;
